import { useCallback } from "react";
import { Auth } from "aws-amplify";
import type {
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import useLogger from "../Logger/useLogger";
import type { MFAMethod } from "./MFA/MFAUtils";

export interface UseCognitoAuthWithLogsReturn {
  getAuthenticatedUser: () => Promise<CognitoUser>;
  changePassword: (
    user: CognitoUser,
    oldPassword: string,
    newPassword: string
  ) => Promise<"SUCCESS">;
  setPreferredMFA: (user: CognitoUser, mfaMethod: MFAMethod) => Promise<string>;
  verifyTotpToken: (
    user: CognitoUser,
    challengeAnswer: string
  ) => Promise<CognitoUserSession>;
  setupTOTP: (user: CognitoUser) => Promise<string>;
  signOut: () => Promise<void>;
}

const useCognitoAuthWithLogs = (): UseCognitoAuthWithLogsReturn => {
  const { logError } = useLogger();

  const logErrorMiddleware = useCallback(
    <T>(promise: Promise<T>): Promise<T> => {
      promise.catch((error) =>
        logError(error, (error as { message?: string })?.message ?? "")
      );
      return promise;
    },
    [logError]
  );

  const getAuthenticatedUser = useCallback(
    () => logErrorMiddleware(Auth.currentAuthenticatedUser()),
    [logErrorMiddleware]
  );

  const changePassword = useCallback(
    (user: CognitoUser, oldPassword: string, newPassword: string) =>
      logErrorMiddleware(Auth.changePassword(user, oldPassword, newPassword)),
    [logErrorMiddleware]
  );

  const setPreferredMFA = useCallback(
    (user: CognitoUser, mfaMethod: MFAMethod) =>
      logErrorMiddleware(Auth.setPreferredMFA(user, mfaMethod)),
    [logErrorMiddleware]
  );

  const verifyTotpToken = useCallback(
    (user: CognitoUser, challengeAnswer: string) =>
      logErrorMiddleware(Auth.verifyTotpToken(user, challengeAnswer)),
    [logErrorMiddleware]
  );

  const setupTOTP = useCallback(
    (user: CognitoUser) => logErrorMiddleware(Auth.setupTOTP(user)),
    [logErrorMiddleware]
  );

  const signOut = useCallback(
    () => logErrorMiddleware(Auth.signOut()),
    [logErrorMiddleware]
  );

  return {
    getAuthenticatedUser,
    changePassword,
    setPreferredMFA,
    verifyTotpToken,
    setupTOTP,
    signOut,
  };
};

export default useCognitoAuthWithLogs;
